import { env } from "@/lib/env"
import { getApiToken } from "./authContext"
import { generateRequestId } from "./requestId"

/**
 * Base API client configuration.
 *
 * In the browser, API calls go SAME-ORIGIN (`/api/*`) and are reverse-proxied
 * to the backend by the Cloudflare Pages Function in `functions/api`. This way
 * the browser only ever connects to this (trusted) frontend origin — never
 * directly to the Railway host, which some networks block / TLS-intercept
 * (net::ERR_CERT_* → "Failed to fetch"). Outside the browser (tests/SSR) there
 * is no proxy, so fall back to the configured absolute URL.
 */

export const API_BASE_URL = typeof window !== "undefined" ? "" : env.VITE_API_BASE_URL

/**
 * Normalized API error type for consistent error handling.
 */
export interface ApiError {
  status: number
  code?: string
  message: string
  details?: unknown
  /** Correlation ID from the request or response (best-effort). */
  requestId?: string
}

/**
 * Type guard to check if an error is an ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "message" in error
  )
}

/**
 * Check if an error is a 401 Unauthorized response.
 */
export function isUnauthorizedError(error: unknown): boolean {
  return isApiError(error) && error.status === 401
}

/**
 * Check if an error is a 403 Forbidden response.
 */
export function isForbiddenError(error: unknown): boolean {
  return isApiError(error) && error.status === 403
}

/**
 * Session-expiry handling.
 *
 * Fires the first time apiRequest sees a 401, then is silent for the rest
 * of the page lifecycle. A React component (Auth0LogoutBridge in
 * providers.tsx) registers a handler that runs Auth0 logout + redirects
 * to /sign-in; without that we fall back to window.location so the user
 * never gets stuck on a broken page reading "Session expired" toasts on
 * a loop.
 */
let sessionExpiryHandler: (() => void) | null = null
let sessionExpiryFired = false

export function setSessionExpiryHandler(handler: () => void): void {
  sessionExpiryHandler = handler
}

function notifySessionExpired(): void {
  if (sessionExpiryFired) return
  sessionExpiryFired = true
  if (sessionExpiryHandler) {
    sessionExpiryHandler()
  } else {
    setTimeout(() => { window.location.href = "/sign-in" }, 800)
  }
}

/**
 * Creates base headers for API requests.
 * X-Request-Id is always included; caller-provided value wins via spread order.
 */
export function createHeaders(token?: string, requestId?: string): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Request-Id": requestId ?? generateRequestId(),
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  return headers
}

/**
 * Attempt to extract a request/correlation ID from the response.
 * Checks response body first, then response header, then falls back to the
 * ID we generated on the outgoing request.
 */
function resolveRequestId(
  body: Record<string, unknown> | null,
  response: Response,
  fallback: string
): string {
  if (body && typeof body.request_id === "string") return body.request_id
  return response.headers.get("x-request-id") ?? fallback
}

/**
 * Safely read and parse a response body.
 * Returns a parsed object for JSON content-types, null otherwise.
 * Never throws — parse failures return null.
 */
async function safeParseBody(
  response: Response
): Promise<Record<string, unknown> | null> {
  if (response.status === 204) return null

  const contentType = response.headers.get("content-type") ?? ""
  if (!contentType.includes("application/json")) {
    return null
  }

  try {
    const body = await response.json()
    if (typeof body === "object" && body !== null) {
      return body as Record<string, unknown>
    }
    return null
  } catch {
    return null
  }
}

/**
 * Generic fetch wrapper for API calls with normalized error handling.
 *
 * Guarantees:
 * - Every request includes an X-Request-Id header (caller-provided wins)
 * - Authorization: Bearer <jwt> when token is provided
 * - 204 No Content returns null without attempting JSON parse
 * - Non-JSON error bodies are handled gracefully (no parse exceptions)
 * - Errors are normalized to ApiError with status, code, message, details, requestId
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const baseHeaders = createHeaders(token)
  // Caller-provided headers (including X-Request-Id) override base headers
  const mergedHeaders = { ...baseHeaders, ...options.headers } as Record<
    string,
    string
  >
  const outgoingRequestId = mergedHeaders["X-Request-Id"]

  // Transient Railway gateway blips (cold-start / proxy hiccup) surface two
  // ways: the connection is dropped (fetch throws "Failed to fetch") or the
  // edge returns a 502/503/504 before the app is reached. Both are retried for
  // idempotent GETs with a short backoff so a momentary blip doesn't surface a
  // hard error. Real HTTP error responses (4xx, app 5xx) are NOT retried.
  const method = (options.method ?? "GET").toUpperCase()
  const maxAttempts = method === "GET" ? 3 : 1
  const TRANSIENT_STATUS = new Set([502, 503, 504])
  let response: Response | undefined
  let lastNetworkErr: unknown
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const r = await fetch(url, { ...options, headers: mergedHeaders })
      if (TRANSIENT_STATUS.has(r.status) && attempt < maxAttempts) {
        await new Promise((res) => setTimeout(res, 600 * attempt))
        continue
      }
      response = r
      break
    } catch (err) {
      lastNetworkErr = err
      if (attempt < maxAttempts) {
        await new Promise((res) => setTimeout(res, 600 * attempt))
        continue
      }
      throw err
    }
  }
  if (!response) throw lastNetworkErr

  if (!response.ok) {
    const body = await safeParseBody(response)

    const rawCode = body?.code ?? body?.error_code ?? body?.error
    const rawMessage = body?.message ?? body?.error ?? body?.detail

    // For 401/403, override the backend wording with a clean user-facing
    // message so that any catch handler that surfaces err.message doesn't
    // leak technical strings like "Authentication required" into the UI.
    // The original backend message is preserved on the error code/details
    // for debugging.
    const cleanMessage =
      response.status === 401
        ? "Session expired. Please sign in again."
        : response.status === 403
        ? "Not authorized to perform this action."
        : typeof rawMessage === "string"
        ? rawMessage
        : response.statusText || "Unknown error"

    const apiError: ApiError = {
      status: response.status,
      code: typeof rawCode === "string" ? rawCode : undefined,
      message: cleanMessage,
      details: body?.details ?? body?.errors ?? undefined,
      requestId: resolveRequestId(body, response, outgoingRequestId),
    }

    // 401 from the API (session/token expired) → trigger a single
    // logout-and-redirect flow. We dedupe inside notifySessionExpired so
    // that even if a dozen queries fire and all 401, only one redirect
    // is scheduled.
    if (response.status === 401) {
      notifySessionExpired()
    }

    throw apiError
  }

  // 204 No Content — no body to parse
  if (response.status === 204) {
    return null as unknown as T
  }

  // Non-JSON success responses (rare but defensive)
  const contentType = response.headers.get("content-type") ?? ""
  if (!contentType.includes("application/json")) {
    return null as unknown as T
  }

  return response.json()
}

/**
 * Authenticated API request.
 * Automatically retrieves and attaches the access token if available.
 *
 * Use this for API calls that should include authentication when in auth mode.
 * In public mode (no token getter configured), requests are made without auth.
 */
export async function authenticatedApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Auth0 may not have minted a token yet on first paint. If getApiToken
  // returns null we briefly retry — absorbs the race that previously
  // surfaced as an "Authentication required" toast (Hans #2).
  let token = await getApiToken()
  if (!token) {
    await new Promise((r) => setTimeout(r, 250))
    token = await getApiToken()
  }
  if (!token) {
    await new Promise((r) => setTimeout(r, 500))
    token = await getApiToken()
  }
  return apiRequest<T>(endpoint, options, token ?? undefined)
}
